import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
  PathElement,
  NodeChildAssociationEntry,
  NodeChildAssociationPaging,
  NodeEntry,
  Node,
} from '@alfresco/js-api';
import { ContentApiService } from '@alfresco/aca-shared';
import { Store } from '@ngrx/store';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

import { WkFileUploadComponent } from '../dialogs/wk-file-upload/wk-file-upload.component';
import { AppStore } from '../store/states/app.state';
import { CONSTANTS } from '../model/custom-adf-model';
import { getAppSelection } from '../store/selectors/app.custom-selectors';
import { ContentService } from '../components/shared/services/content.service';
import { WkImagePreviewDialogComponent } from '../dialogs/wk-image-preview/wk-image-preview.dialog';
import { ImagePreviewDetails } from '../types';

@Injectable({
  providedIn: 'root',
})
export class InsertMediaService {
  _imageDetails = new BehaviorSubject<any>('');
  _videoDetails = new BehaviorSubject<any>('');
  _audioDetails = new BehaviorSubject<any>('');
  _folderDetails = new BehaviorSubject<any>('');
  selectedNode = new Map();
  private readonly selectedMedia$ = new BehaviorSubject<Node | null>(null);
  togglePanel = false;
  buttonEnable = new Subject<boolean>();
  mediaProperties = new BehaviorSubject<any>('');
  updateImageProperties = new BehaviorSubject<any>('');
  disableAlignment = new BehaviorSubject<boolean>(true);
  audio: HTMLAudioElement;
  private readonly onDestroy$ = new Subject<boolean>();
  mediaElement = new BehaviorSubject<any>('');
  private mediaType = '';
  audioVideoElement = new BehaviorSubject<any>('');
  updateMediaProperties = new BehaviorSubject<any>('');
  saveDisable = new BehaviorSubject<boolean>(false);
  mediaSaveDisable = new BehaviorSubject<boolean>(false);
  rootName = 'Sites';
  constructor(
    private readonly dialogRef: MatDialog,
    private readonly customContentApi: ContentApiService,
    private readonly store: Store<AppStore>,
    private readonly contentService: ContentService
  ) {
    this.audio = new Audio();
  }

  getMediaType() {
    return this.mediaType;
  }

  setMediaType(type: string) {
    this.mediaType = type;
  }

  getClickedFolderDetails() {
    return this._folderDetails.asObservable();
  }

  setClickedFolderDetails(data: any) {
    this._folderDetails.next(data);
  }

  getImageDetails() {
    return this._imageDetails.asObservable();
  }

  setImageDetails(data: any) {
    this._imageDetails.next(data);
  }

  getVideoDetails() {
    return this._videoDetails.asObservable();
  }

  setVideoDetails(data: any) {
    this._videoDetails.next(data);
  }

  getAudioDetails() {
    return this._audioDetails.asObservable();
  }

  setAudioDetails(data: any) {
    this._audioDetails.next(data);
  }

  getMediaProperties() {
    return this.mediaProperties.asObservable();
  }

  setMediaProperties(data: any) {
    this.mediaProperties.next(data);
  }

  setMediaElement(data: any) {
    this.mediaElement.next(data);
  }

  getMediaElement() {
    return this.mediaElement.asObservable();
  }

  getUpdatedImageProperties() {
    return this.updateImageProperties.asObservable();
  }

  setUpdatedImageProperties(data: any) {
    this.updateImageProperties.next(data);
  }

  getUpdatedMediaProperties() {
    return this.updateMediaProperties.asObservable();
  }

  setUpdatedMediaProperties(data: any) {
    this.updateMediaProperties.next(data);
  }

  getDisable() {
    return this.disableAlignment.asObservable();
  }

  setDisable(data: boolean) {
    this.disableAlignment.next(data);
  }

  getSaveDisable() {
    return this.saveDisable.asObservable();
  }

  setSaveDisable(data: boolean) {
    return this.saveDisable.next(data);
  }

  getSaveForMedia() {
    return this.mediaSaveDisable.asObservable();
  }

  setSaveForMedia(data: boolean) {
    return this.mediaSaveDisable.next(data);
  }

  openUploadDialog(fileNames, node) {
    this.dialogRef.open(WkFileUploadComponent, {
      data: {
        directoryName: node,
        fileNamesToUpload: fileNames,
      },
      width: '739px',
      height: '419px',
    });
  }

  openPreviewDialog(event) {
    const imageElement = event.currentTarget.parentElement.firstChild;
    const selectedImageModal = imageElement.querySelector('img');
    const imageDetails: ImagePreviewDetails = {
      src:
        selectedImageModal.getAttribute('src') ||
        imageElement.getAttribute('src'),
      altTxt:
        selectedImageModal.getAttribute('alt') ||
        imageElement.getAttribute('alt'),
    };
    this.dialogRef.open(WkImagePreviewDialogComponent, {
      data: { imageDetails },
      width: '80%',
      panelClass: 'mat-dialog-image',
    });
  }

  getNodeElements() {
    return new Promise((resolve) => {
      this.store
        .select(getAppSelection)
        .pipe(
          take(1),
          switchMap((data: any) => {
            const id = data.file.entry.id;
            return this.customContentApi.getNodeInfo(id, {
              include: ['path'],
            });
          }),
          takeUntil(this.onDestroy$)
        )
        .subscribe(async (nodeEntry) => {
          const parentElements = nodeEntry.path?.elements;
          const rootNode = parentElements?.find(
            (root) =>
              root.name === CONSTANTS.DOCUMENT_LIBRARY ||
              root.name === CONSTANTS.EMAIL_TEMPLATES ||
              root.name === CONSTANTS.CONTRACT_TEMPLATES ||
              root.name === CONSTANTS.CONTENT_TEMPLATES
          );
          resolve(
            firstValueFrom(
              this.customContentApi.getNodeInfo(rootNode?.id, {
                include: ['path'],
              })
            )
          );
        });
    });
  }

  onPlay(src) {
    this.audio.src = src;
    this.audio.load();
    this.audio.play();
    return this.audio;
  }

  onPause() {
    this.audio.pause();
  }

  getBreadCrumb(
    route: PathElement
  ): Promise<NodeChildAssociationEntry | NodeEntry> {
    if (route.name === 'APP.BROWSE.LIBRARIES.MENU.MY_LIBRARIES.TITLE') {
      return firstValueFrom(
        this.customContentApi
          .getNodeChildren(route.id)
          .pipe(
            map((data: NodeChildAssociationPaging) =>
              data.list.entries.find(
                (item: NodeChildAssociationEntry) =>
                  item.entry.name === this.rootName
              )
            )
          )
      );
    } else {
      return firstValueFrom(this.contentService.getNode(route.id));
    }
  }

  public selectMedia(node: Node | null): void {
    this.selectedMedia$.next(node);
  }

  public unselectedMedia(): void {
    this.selectedMedia$.next(null);
  }

  public getSelectedMedia(): Observable<Node | null> {
    return this.selectedMedia$.asObservable();
  }

  public getSelectedMediaValue(): Node | null {
    return this.selectedMedia$.getValue();
  }
}
